import React from 'react';
import {createPortal} from 'react-dom';
import styles from '../Profile.module.css';

interface ProfilePictureSourceModalProps {
  hasProfilePhoto?: boolean;
  isRemovingPhoto?: boolean;
  removePhotoError?: string | null;
  onClose: () => void;
  onNativeCameraFallback: () => void;
  onPhotoCaptured: (file: File) => void | Promise<void>;
  onRemovePhoto?: () => void | Promise<void>;
  onUploadPhoto: () => void;
}

type CameraModalMode =
  | 'chooser'
  | 'camera-loading'
  | 'camera-ready'
  | 'camera-error'
  | 'captured'
  | 'remove-confirm';

const PROFILE_CAMERA_JPEG_QUALITY = 0.92;
const PROFILE_CAMERA_MAX_DIMENSION = 1920;
const PROFILE_CAMERA_REQUEST_TIMEOUT_MS = 15000;
const PROFILE_CAMERA_PREVIEW_TIMEOUT_MS = 10000;
const cameraPhotoSolidIconUrl = `${process.env.PUBLIC_URL}/icons/camera-photo-solid.svg`;
const uploadPictureIconUrl = `${process.env.PUBLIC_URL}/icons/upload-picture.svg`;
const deletePhotoIconUrl = `${process.env.PUBLIC_URL}/icons/baseline-delete.svg`;
const sourceIconStyle = (iconUrl: string) => ({
  '--profile-picture-source-icon-url': `url("${iconUrl}")`,
}) as React.CSSProperties;

const stopMediaStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach(track => track.stop());
};

const getCameraErrorMessage = (error: unknown) => {
  if (window.isSecureContext === false) {
    return 'Camera access requires a secure HTTPS connection. Upload a photo instead.';
  }

  const errorName = error instanceof DOMException
    ? error.name
    : typeof error === 'object' && error && 'name' in error
      ? String(error.name)
      : '';

  switch (errorName) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'Camera access was blocked. Allow camera access in your browser settings, then try again.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No camera was found on this device.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Your camera is already in use or could not be started.';
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'Your camera does not support the requested settings.';
    default:
      return 'The camera could not be opened. Check your browser permissions and try again.';
  }
};

const createCapturedPhotoFile = (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
) => new Promise<File>((resolve, reject) => {
  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;

  if (sourceWidth <= 0 || sourceHeight <= 0) {
    reject(new Error('The camera is not ready to take a photo yet.'));
    return;
  }

  const captureScale = Math.min(1, PROFILE_CAMERA_MAX_DIMENSION / Math.max(sourceWidth, sourceHeight));
  canvas.width = Math.max(1, Math.round(sourceWidth * captureScale));
  canvas.height = Math.max(1, Math.round(sourceHeight * captureScale));
  const context = canvas.getContext('2d');

  if (!context) {
    reject(new Error('This browser could not capture the camera photo.'));
    return;
  }

  // The preview is mirrored with CSS for a natural selfie experience. Drawing
  // the source directly keeps the saved photo correctly oriented.
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('This browser could not capture the camera photo.'));
      return;
    }

    resolve(new File([blob], `profile-photo-${Date.now()}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    }));
  }, 'image/jpeg', PROFILE_CAMERA_JPEG_QUALITY);
});

const CameraIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M8.5 5.5 10 3.8h4l1.5 1.7H19A2.5 2.5 0 0 1 21.5 8v9A2.5 2.5 0 0 1 19 19.5H5A2.5 2.5 0 0 1 2.5 17V8A2.5 2.5 0 0 1 5 5.5h3.5ZM12 16.3a4.3 4.3 0 1 0 0-8.6 4.3 4.3 0 0 0 0 8.6Zm0-2a2.3 2.3 0 1 1 0-4.6 2.3 2.3 0 0 1 0 4.6Z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6.3 6.3a1 1 0 0 1 1.4 0L12 10.6l4.3-4.3a1 1 0 1 1 1.4 1.4L13.4 12l4.3 4.3a1 1 0 0 1-1.4 1.4L12 13.4l-4.3 4.3a1 1 0 0 1-1.4-1.4l4.3-4.3-4.3-4.3a1 1 0 0 1 0-1.4Z"
      fill="currentColor"
    />
  </svg>
);

const ProfilePictureSourceModal: React.FC<ProfilePictureSourceModalProps> = ({
  hasProfilePhoto = false,
  isRemovingPhoto = false,
  removePhotoError = null,
  onClose,
  onNativeCameraFallback,
  onPhotoCaptured,
  onRemovePhoto,
  onUploadPhoto,
}) => {
  const [mode, setMode] = React.useState<CameraModalMode>('chooser');
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [cameraStream, setCameraStream] = React.useState<MediaStream | null>(null);
  const [capturedFile, setCapturedFile] = React.useState<File | null>(null);
  const [capturedPhotoUrl, setCapturedPhotoUrl] = React.useState<string | null>(null);
  const [isCapturing, setIsCapturing] = React.useState(false);
  const dialogRef = React.useRef<HTMLElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const primaryActionRef = React.useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const mountedRef = React.useRef(true);
  const cameraRequestIdRef = React.useRef(0);
  const streamRef = React.useRef<MediaStream | null>(null);
  const capturedPhotoUrlRef = React.useRef<string | null>(null);
  const previouslyFocusedElementRef = React.useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  const clearCapturedPhoto = React.useCallback(() => {
    setCapturedFile(null);
    if (capturedPhotoUrlRef.current) {
      URL.revokeObjectURL(capturedPhotoUrlRef.current);
      capturedPhotoUrlRef.current = null;
    }
    setCapturedPhotoUrl(null);
  }, []);

  const stopCamera = React.useCallback(() => {
    cameraRequestIdRef.current += 1;
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    setCameraStream(null);

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const handleClose = React.useCallback(() => {
    if (isRemovingPhoto) {
      return;
    }

    stopCamera();
    clearCapturedPhoto();
    onClose();
  }, [clearCapturedPhoto, isRemovingPhoto, onClose, stopCamera]);

  const handleUploadPhoto = React.useCallback(() => {
    stopCamera();
    clearCapturedPhoto();
    onUploadPhoto();
  }, [clearCapturedPhoto, onUploadPhoto, stopCamera]);

  const startCamera = React.useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      stopCamera();
      clearCapturedPhoto();
      onNativeCameraFallback();
      return;
    }

    stopCamera();
    clearCapturedPhoto();
    setCameraError(null);
    setIsCapturing(false);
    setMode('camera-loading');
    const requestId = cameraRequestIdRef.current + 1;
    cameraRequestIdRef.current = requestId;
    const requestTimeoutId = window.setTimeout(() => {
      if (!mountedRef.current || cameraRequestIdRef.current !== requestId) {
        return;
      }

      cameraRequestIdRef.current += 1;
      setCameraError('Camera access is taking too long. Check for a browser permission prompt, then try again.');
      setMode('camera-error');
    }, PROFILE_CAMERA_REQUEST_TIMEOUT_MS);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: {ideal: 'user'},
        },
      });

      if (!mountedRef.current || cameraRequestIdRef.current !== requestId) {
        stopMediaStream(stream);
        return;
      }

      streamRef.current = stream;
      setCameraStream(stream);
    } catch (error) {
      if (!mountedRef.current || cameraRequestIdRef.current !== requestId) {
        return;
      }

      setCameraError(getCameraErrorMessage(error));
      setMode('camera-error');
    } finally {
      window.clearTimeout(requestTimeoutId);
    }
  }, [clearCapturedPhoto, onNativeCameraFallback, stopCamera]);

  React.useEffect(() => {
    const video = videoRef.current;

    if (!video || !cameraStream) {
      return;
    }

    let isCanceled = false;
    const markPreviewReady = () => {
      if (
        isCanceled ||
        streamRef.current !== cameraStream ||
        video.videoWidth <= 0 ||
        video.videoHeight <= 0
      ) {
        return;
      }

      window.clearTimeout(previewTimeoutId);
      setMode('camera-ready');
    };
    const handlePreviewFailure = () => {
      if (isCanceled || streamRef.current !== cameraStream || !mountedRef.current) {
        return;
      }

      stopCamera();
      setCameraError('The camera preview could not start. Try again or upload a photo instead.');
      setMode('camera-error');
    };
    const previewTimeoutId = window.setTimeout(() => {
      if (isCanceled || streamRef.current !== cameraStream || !mountedRef.current) {
        return;
      }

      stopCamera();
      setCameraError('The camera started but no preview appeared. Try again or upload a photo instead.');
      setMode('camera-error');
    }, PROFILE_CAMERA_PREVIEW_TIMEOUT_MS);

    video.addEventListener('loadedmetadata', markPreviewReady);
    video.addEventListener('loadeddata', markPreviewReady);
    video.addEventListener('canplay', markPreviewReady);
    video.addEventListener('playing', markPreviewReady);
    video.srcObject = cameraStream;
    void video.play().then(markPreviewReady).catch(handlePreviewFailure);

    // Some browsers already have frame dimensions by the time srcObject is
    // assigned and do not emit another readiness event.
    markPreviewReady();

    return () => {
      isCanceled = true;
      window.clearTimeout(previewTimeoutId);
      video.removeEventListener('loadedmetadata', markPreviewReady);
      video.removeEventListener('loadeddata', markPreviewReady);
      video.removeEventListener('canplay', markPreviewReady);
      video.removeEventListener('playing', markPreviewReady);
    };
  }, [cameraStream, stopCamera]);

  React.useEffect(() => {
    // React Strict Mode runs this effect's cleanup and setup twice in
    // development. Reset the mounted flag during every setup so a valid
    // camera stream is not mistaken for a late result from an unmounted modal.
    mountedRef.current = true;
    const previousBodyOverflow = document.body.style.overflow;
    const previouslyFocusedElement = previouslyFocusedElementRef.current;
    document.body.style.overflow = 'hidden';

    return () => {
      mountedRef.current = false;
      cameraRequestIdRef.current += 1;
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      if (capturedPhotoUrlRef.current) {
        URL.revokeObjectURL(capturedPhotoUrlRef.current);
        capturedPhotoUrlRef.current = null;
      }
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocusedElement?.focus();
    };
  }, []);

  React.useEffect(() => {
    (primaryActionRef.current || closeButtonRef.current)?.focus();
  }, [mode]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  const handleCapture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || isCapturing) {
      return;
    }

    setIsCapturing(true);
    setCameraError(null);

    try {
      const file = await createCapturedPhotoFile(video, canvas);
      const previewUrl = URL.createObjectURL(file);
      stopCamera();
      capturedPhotoUrlRef.current = previewUrl;
      setCapturedFile(file);
      setCapturedPhotoUrl(previewUrl);
      setMode('captured');
    } catch (error) {
      setCameraError(error instanceof Error ? error.message : 'The photo could not be captured.');
    } finally {
      if (mountedRef.current) {
        setIsCapturing(false);
      }
    }
  };

  const handleRetake = () => {
    void startCamera();
  };

  const handleUsePhoto = () => {
    if (!capturedFile) {
      return;
    }

    const file = capturedFile;
    stopCamera();
    clearCapturedPhoto();
    void onPhotoCaptured(file);
  };

  const isCameraMode = mode === 'camera-loading' || mode === 'camera-ready';
  const title = mode === 'chooser'
    ? 'Profile Photo'
    : mode === 'captured'
      ? 'Use This Photo?'
      : mode === 'camera-error'
        ? 'Camera Unavailable'
        : mode === 'remove-confirm'
          ? 'Remove Profile Photo?'
          : 'Take Photo';

  return createPortal(
    <div
      className={styles.profilePictureSourceBackdrop}
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <section
        ref={dialogRef}
        className={`${styles.profilePictureSourceCard} ${
          mode === 'chooser' ? '' : styles.profilePictureCameraCard
        }`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-picture-source-title"
        aria-describedby="profile-picture-source-description"
      >
        <div className={styles.profilePictureSourceHeader}>
          <div>
            <h2 id="profile-picture-source-title" className={styles.profilePictureSourceTitle}>
              {title}
            </h2>
            <p
              id="profile-picture-source-description"
              className={styles.profilePictureSourceDescription}
            >
              {mode === 'chooser'
                ? 'Choose how you want to add your photo.'
                : mode === 'captured'
                  ? 'Review your photo before continuing to crop it.'
                  : mode === 'camera-error'
                    ? 'You can retry the camera or upload a photo instead.'
                    : mode === 'remove-confirm'
                      ? 'Your current photo will be permanently removed and replaced with your profile initial.'
                      : 'Center your face in the preview, then capture your photo.'}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.profilePictureSourceClose}
            onClick={handleClose}
            disabled={isRemovingPhoto}
            aria-label="Close profile photo options"
          >
            <CloseIcon />
          </button>
        </div>

        {mode === 'chooser' ? (
          <div className={styles.profilePictureSourceActions}>
            <button
              ref={primaryActionRef}
              type="button"
              className={styles.profilePictureSourceOption}
              onClick={() => void startCamera()}
            >
              <span className={styles.profilePictureSourceIcon}>
                <span
                  className={styles.profilePictureSourceIconGlyph}
                  style={sourceIconStyle(cameraPhotoSolidIconUrl)}
                  aria-hidden="true"
                />
              </span>
              <span>Take Photo</span>
            </button>
            <button
              type="button"
              className={styles.profilePictureSourceOption}
              onClick={handleUploadPhoto}
            >
              <span className={styles.profilePictureSourceIcon}>
                <span
                  className={styles.profilePictureSourceIconGlyph}
                  style={sourceIconStyle(uploadPictureIconUrl)}
                  aria-hidden="true"
                />
              </span>
              <span>Upload Photo</span>
            </button>
            {hasProfilePhoto && onRemovePhoto ? (
              <button
                type="button"
                className={`${styles.profilePictureSourceOption} ${styles.profilePictureSourceRemoveOption}`}
                onClick={() => setMode('remove-confirm')}
              >
                <span className={`${styles.profilePictureSourceIcon} ${styles.profilePictureSourceRemoveIcon}`}>
                  <span
                    className={styles.profilePictureSourceIconGlyph}
                    style={sourceIconStyle(deletePhotoIconUrl)}
                    aria-hidden="true"
                  />
                </span>
                <span>Remove Photo</span>
              </button>
            ) : null}
          </div>
        ) : null}

        {mode === 'remove-confirm' && onRemovePhoto ? (
          <div className={styles.profilePictureRemoveConfirmation}>
            <div className={styles.profilePictureRemoveNotice}>
              <span className={styles.profilePictureRemoveNoticeIcon} aria-hidden="true">
                <span
                  className={styles.profilePictureSourceIconGlyph}
                  style={sourceIconStyle(deletePhotoIconUrl)}
                />
              </span>
              <p>This action cannot be undone. You can upload a new profile photo at any time.</p>
            </div>
            {removePhotoError ? (
              <p className={styles.profilePictureRemoveError} role="alert">
                {removePhotoError}
              </p>
            ) : null}
            <div className={styles.profilePictureRemoveActions}>
              <button
                ref={primaryActionRef}
                type="button"
                className={styles.profilePictureCameraSecondaryButton}
                onClick={() => setMode('chooser')}
                disabled={isRemovingPhoto}
              >
                Keep Photo
              </button>
              <button
                type="button"
                className={styles.profilePictureRemoveButton}
                onClick={() => void onRemovePhoto()}
                disabled={isRemovingPhoto}
              >
                {isRemovingPhoto ? 'Removing...' : 'Remove Photo'}
              </button>
            </div>
          </div>
        ) : null}

        {isCameraMode ? (
          <>
            <div className={styles.profilePictureCameraStage}>
              <video
                ref={videoRef}
                className={styles.profilePictureCameraVideo}
                autoPlay
                muted
                playsInline
              />
              {mode === 'camera-loading' ? (
                <div className={styles.profilePictureCameraLoading} role="status">
                  <span className={styles.profilePictureCameraSpinner} />
                  Starting camera…
                </div>
              ) : null}
            </div>
            {cameraError ? (
              <p className={styles.profilePictureCameraInlineError} role="alert">
                {cameraError}
              </p>
            ) : null}
            <div className={styles.profilePictureCameraActions}>
              <button
                type="button"
                className={styles.profilePictureCameraSecondaryButton}
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                ref={mode === 'camera-ready' ? primaryActionRef : undefined}
                type="button"
                className={styles.profilePictureCameraPrimaryButton}
                onClick={() => void handleCapture()}
                disabled={mode !== 'camera-ready' || isCapturing}
              >
                {isCapturing ? 'Capturing…' : 'Capture'}
              </button>
            </div>
          </>
        ) : null}

        {mode === 'captured' && capturedPhotoUrl ? (
          <>
            <div className={styles.profilePictureCameraStage}>
              <img
                className={styles.profilePictureCapturedImage}
                src={capturedPhotoUrl}
                alt="Captured profile preview"
              />
            </div>
            <div className={styles.profilePictureCameraActionsThree}>
              <button
                type="button"
                className={styles.profilePictureCameraSecondaryButton}
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.profilePictureCameraSecondaryButton}
                onClick={handleRetake}
              >
                Retake
              </button>
              <button
                ref={primaryActionRef}
                type="button"
                className={styles.profilePictureCameraPrimaryButton}
                onClick={handleUsePhoto}
              >
                Use Photo
              </button>
            </div>
          </>
        ) : null}

        {mode === 'camera-error' ? (
          <div className={styles.profilePictureCameraError} role="alert">
            <span className={styles.profilePictureCameraErrorIcon}>
              <CameraIcon />
            </span>
            <p>{cameraError}</p>
            <div className={styles.profilePictureCameraErrorActions}>
              <button
                ref={primaryActionRef}
                type="button"
                className={styles.profilePictureCameraPrimaryButton}
                onClick={() => void startCamera()}
              >
                Try Again
              </button>
              <button
                type="button"
                className={styles.profilePictureCameraSecondaryButton}
                onClick={handleUploadPhoto}
              >
                Upload Photo Instead
              </button>
            </div>
          </div>
        ) : null}

        <canvas ref={canvasRef} hidden />
      </section>
    </div>,
    document.body,
  );
};

export default React.memo(ProfilePictureSourceModal);
